import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import axios from "axios";
import { 
  CheckCircle, 
  Leaf,
  ArrowRight,
  Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionSuccess() {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId && token) {
      pollPaymentStatus(sessionId);
    } else if (!token) {
      navigate("/login");
    }
  }, [searchParams, token]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    setPollCount(attempts + 1);

    try {
      const response = await axios.get(`${API}/subscription/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setPaymentInfo(response.data);

      if (response.data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (response.data.status === "expired") {
        setStatus("expired");
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error("Error checking payment:", error);
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <Card className="max-w-lg w-full">
        <CardContent className="p-8 text-center">
          {status === "checking" && (
            <>
              <Loader2 className="w-16 h-16 mx-auto text-primary animate-spin mb-6" />
              <h1 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>
                Vérification du paiement...
              </h1>
              <p className="text-muted-foreground">
                Veuillez patienter pendant que nous confirmons votre paiement.
              </p>
              <p className="text-sm text-muted-foreground mt-4">
                Tentative {pollCount}/5
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-20 h-20 mx-auto rounded-full bg-chart-1/10 flex items-center justify-center mb-6">
                <CheckCircle className="w-12 h-12 text-chart-1" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>
                Paiement réussi !
              </h1>
              <p className="text-muted-foreground mb-6">
                Votre abonnement est maintenant actif. Vous avez accès à toutes les fonctionnalités de votre plan.
              </p>
              {paymentInfo && (
                <div className="bg-muted/50 rounded-xl p-4 mb-6">
                  <p className="text-sm text-foreground">
                    Montant: <strong>{paymentInfo.amount}€</strong>
                  </p>
                </div>
              )}
              <div className="flex flex-col gap-3">
                <Button 
                  onClick={() => navigate("/dashboard")} 
                  className="rounded-full"
                  data-testid="go-dashboard-btn"
                >
                  Accéder au dashboard
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate("/pricing")}
                  className="rounded-full"
                >
                  Voir mon abonnement
                </Button>
              </div>
            </>
          )}

          {status === "timeout" && (
            <>
              <div className="w-20 h-20 mx-auto rounded-full bg-chart-4/10 flex items-center justify-center mb-6">
                <Loader2 className="w-12 h-12 text-chart-4" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>
                Vérification en cours
              </h1>
              <p className="text-muted-foreground mb-6">
                La vérification prend plus de temps que prévu. Votre paiement sera confirmé par email.
              </p>
              <Button 
                onClick={() => navigate("/dashboard")} 
                className="rounded-full"
              >
                Aller au dashboard
              </Button>
            </>
          )}

          {status === "expired" && (
            <>
              <div className="w-20 h-20 mx-auto rounded-full bg-destructive/10 flex items-center justify-center mb-6">
                <Leaf className="w-12 h-12 text-destructive" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>
                Session expirée
              </h1>
              <p className="text-muted-foreground mb-6">
                Votre session de paiement a expiré. Veuillez réessayer.
              </p>
              <Button 
                onClick={() => navigate("/pricing")} 
                className="rounded-full"
              >
                Retourner aux tarifs
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-20 h-20 mx-auto rounded-full bg-destructive/10 flex items-center justify-center mb-6">
                <Leaf className="w-12 h-12 text-destructive" />
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope' }}>
                Erreur
              </h1>
              <p className="text-muted-foreground mb-6">
                Une erreur s'est produite lors de la vérification. Contactez le support si le problème persiste.
              </p>
              <Button 
                onClick={() => navigate("/pricing")} 
                className="rounded-full"
              >
                Retourner aux tarifs
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
